import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { UserPolicy } from './types/UserPolicy';
import { TeamMemberPolicy } from './types/TeamMemberPolicy';
import { hasSystemPolicy, hasTeamPolicy } from '../utility';
import '../localization/config';
import { useTranslation } from 'react-i18next';
import { forgotPassword, resetPassword } from './api';

export interface IAuthenticationContext {
  token: string | undefined;
  email: string | undefined;
  loginFailed: boolean;
  retryCount: number;
  systemAdmin: boolean;
  userManager: boolean;
  projectManager: boolean;
  resetMessage: string | undefined;
  resetToken: string | undefined;

  isAuthenticated(): boolean;
  onLogin(_email: string, _password: string): void;
  onResetPassword(_values: ResetPassword): void;
  onLogout(): void;
  onForgotPassword(_email: string): void;
  canCheckoutResources(_teamId: number): boolean;
  canCreateResources(_teamId: number): boolean;
  canDeleteResources(_teamId: number): boolean;
  canManageTeamMembers(_teamId: number): boolean;
}

export interface IAuthenticationProviderProps {
  children: any;
}

type TeamMembership = {
  team_member_id: number;
  team_id: number;
  project_id: number;
};

type LoginResponse = {
  token: string;
  email: string;
  id: number;
  team_membership: TeamMembership[];
  user_policies: UserPolicy[];
  team_policies: TeamMemberPolicy[];
};

export type ResetPassword = {
  email: string;
  password: string;
  password_confirmation: string;
  token?: string;
};

const parseQuery = (queryString: any) => {
  let query: any = {};
  let pairs = (
    queryString[0] === '?' ? queryString.substring(1) : queryString
  ).split('&');
  for (let i = 0; i < pairs.length; i++) {
    let pair = pairs[i].split('=');
    query[decodeURIComponent(pair[0])] = decodeURIComponent(pair[1] || '');
  }
  return query;
};

const AuthenticationContext = React.createContext<IAuthenticationContext>({
  token: undefined,
  email: undefined,
  loginFailed: false,
  retryCount: 0,
  systemAdmin: false,
  userManager: false,
  projectManager: false,
  resetMessage: undefined,
  resetToken: undefined,

  onLogin(_email: string, _password: string): void {},
  onResetPassword(_values: ResetPassword): void {},
  onLogout(): void {},
  onForgotPassword(_email: string): void {},
  isAuthenticated(): boolean {
    return false;
  },
  canCheckoutResources(_teamId: number): boolean {
    return false;
  },
  canCreateResources(_teamId: number): boolean {
    return false;
  },
  canDeleteResources(_teamId: number): boolean {
    return false;
  },
  canManageTeamMembers(_teamId: number): boolean {
    return false;
  },
});

const AuthenticationProvider = (props: IAuthenticationProviderProps) => {
  const { children } = props;

  const { t } = useTranslation();

  const [token, setToken] = useState<string | undefined>();
  const [email, setEmail] = useState<string | undefined>();
  const [systemAdmin, setSystemAdmin] = useState<boolean>(false);
  const [userManager, setUserManager] = useState<boolean>(false);
  const [projectManager, setProjectManager] = useState<boolean>(false);
  const [userInfo, setUserInfo] = useState<LoginResponse | undefined>();
  const [loginFailed, setLoginFailed] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [resetMessage, setResetMessage] = useState<string | undefined>();
  const [resetToken, setResetToken] = useState<string | undefined>();

  useEffect(() => {
    const saveToken = sessionStorage.getItem('token');
    const saveUserInfo = sessionStorage.getItem('userInfo');

    const searchParams = parseQuery(location.search);

    if (searchParams.token) {
      setResetToken(searchParams.token);
      if (searchParams.type === 'new-user') {
        setResetMessage(
          `${t('authenticate.newUserMessage1')} ${
            searchParams.firstName || ''
          }${t('authenticate.newUserMessage2')}`
        );
      } else {
        setResetMessage(t('authenticate.passwordResetMessage'));
      }
    }

    // Respond to the `storage` event
    function storageEventHandler(event: any) {
      if (event.key === 'token') {
        setToken(event.newValue);
      }
    }

    window.addEventListener('storage', storageEventHandler);

    if (saveToken) {
      setToken(saveToken);
      if (saveUserInfo) {
        const info = JSON.parse(saveUserInfo);
        setEmail(info.email);
        setSystemAdmin(hasSystemPolicy('FCC_Admin ', info.user_policies));
        setUserManager(
          hasSystemPolicy('FCC_CanManageUsers', info.user_policies)
        );
        setProjectManager(
          hasSystemPolicy('FCC_CanManageProjects', info.user_policies)
        );
        setUserInfo(info);
      }
    }

    return () => {
      // Remove the handler when the component unmounts
      window.removeEventListener('storage', storageEventHandler);
    };
  }, [t, token]);

  const isAuthenticated = () => {
    return !!(token && token.length > 0);
  };

  async function onLogin(email: string, password: string) {
    const count = retryCount + 1;
    try {
      const { data } = await axios.post<LoginResponse>('/api/auth/login', {
        email,
        password,
      });

      setToken(data.token);
      sessionStorage.setItem('token', data.token);
      sessionStorage.setItem('userInfo', JSON.stringify(data));
      setEmail(data.email);
      setUserInfo(data);
      setUserManager(hasSystemPolicy('FCC_CanManageUsers', data.user_policies));
      setProjectManager(
        hasSystemPolicy('FCC_CanManageProjects', data.user_policies)
      );
      setLoginFailed(false);
      setRetryCount(count);
    } catch (error) {
      console.log(error);
      setToken(undefined);
      setEmail(undefined);
      setUserInfo(undefined);
      setUserManager(false);
      setProjectManager(false);
      setLoginFailed(true);
      setRetryCount(count);
    }
  }

  const onResetPassword = (values: ResetPassword) => {
    resetPassword({ ...values, token: resetToken as string })
      .then((_data: any) => {
        onLogin(values.email, values.password)
          .then()
          .catch((err) => console.log(err));
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  const onLogout = () => {
    setToken(undefined);
    sessionStorage.clear();
  };

  const onForgotPassword = (email: string) => {
    forgotPassword( email )
      .then((_data: any) => {
        setToken(undefined);
        sessionStorage.clear();
      })
      .catch((err: any) => {
        console.log(err);
      });
  }

  const canCheckoutResources = (teamId: number) => {
    if (userInfo) {
      return hasTeamPolicy(
        'FCC_CanCheckoutResources',
        teamId,
        userInfo.team_policies
      );
    }

    return false;
  };

  const canCreateResources = (teamId: number) => {
    if (userInfo) {
      return hasTeamPolicy(
        'FCC_CanCreateResources',
        teamId,
        userInfo.team_policies
      );
    }

    return false;
  };

  const canDeleteResources = (teamId: number) => {
    if (userInfo) {
      return hasTeamPolicy(
        'FCC_CanDeleteResources',
        teamId,
        userInfo.team_policies
      );
    }

    return false;
  };

  const canManageTeamMembers = (teamId: number) => {
    if (userInfo) {
      if(systemAdmin) {
        return true;
      }

      return hasTeamPolicy(
        'FCC_CanManageTeamMembers',
        teamId,
        userInfo.team_policies
      );
    }

    return false;
  };

  return (
    <AuthenticationContext.Provider
      value={{
        token,
        loginFailed,
        retryCount,
        email,
        systemAdmin,
        userManager,
        projectManager,
        resetMessage,
        resetToken,

        onLogin,
        onResetPassword,
        onLogout,
        onForgotPassword,
        isAuthenticated,
        canCheckoutResources,
        canCreateResources,
        canDeleteResources,
        canManageTeamMembers,
      }}
    >
      {children}
    </AuthenticationContext.Provider>
  );
};

export { AuthenticationContext, AuthenticationProvider };
